import {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlow,
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeProps,
  type ReactFlowInstance
} from "@xyflow/react";
import { Plus, Redo2, Trash2, Undo2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { TaskGraph } from "../../lib/schemas";

const nodeKinds = [
  "llm",
  "mcp_tool",
  "a2a_delegation",
  "router",
  "join",
  "transform",
  "reviewer",
  "approval"
] as const;

type NodeKind = (typeof nodeKinds)[number];
type GraphNode = TaskGraph["nodes"][number];
interface EditorData extends Record<string, unknown> {
  graphNode: GraphNode;
  label: string;
  kind: NodeKind;
}
type EditorNode = Node<EditorData, "taskGraph">;

const kindLabels: Record<NodeKind, string> = {
  llm: "LLM",
  mcp_tool: "MCP tool",
  a2a_delegation: "A2A delegate",
  router: "Router",
  join: "Join",
  transform: "Transform",
  reviewer: "Reviewer",
  approval: "Approval"
};

const kindConfiguration: Record<NodeKind, unknown> = {
  llm: { endpoint: "chat_completions", request: { model: "" } },
  mcp_tool: { server: "", tool: "", arguments: {} },
  a2a_delegation: { agent: "", request: {} },
  router: { selected: "" },
  join: {},
  transform: {},
  reviewer: { endpoint: "chat_completions", request: { model: "" } },
  approval: {}
};

function kindLabel(kind: string): string {
  return nodeKinds.includes(kind as NodeKind) ? kindLabels[kind as NodeKind] : kind;
}

function configurationObject(configuration: unknown): Record<string, unknown> {
  return typeof configuration === "object" &&
    configuration !== null &&
    !Array.isArray(configuration)
    ? (configuration as Record<string, unknown>)
    : {};
}

function stringField(configuration: Record<string, unknown>, key: string): string {
  return typeof configuration[key] === "string" ? configuration[key] : "";
}

function edgeId(edge: TaskGraph["edges"][number]): string {
  return `${edge.from}:${edge.to}`;
}

export function updateGraphEdgeCondition(
  graph: TaskGraph,
  id: string,
  condition: string
): TaskGraph {
  return {
    ...graph,
    edges: graph.edges.map((edge) =>
      edgeId(edge) === id ? { ...edge, condition: condition.trim() || null } : edge
    )
  };
}

function TaskGraphNode({ data, selected }: NodeProps<EditorNode>) {
  return (
    <div className={`task-graph-node kind-${data.kind}${selected ? " is-selected" : ""}`}>
      <Handle type="target" position={Position.Left} />
      <span>{kindLabels[data.kind]}</span>
      <strong>{data.label}</strong>
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

const nodeTypes = { taskGraph: TaskGraphNode };

function graphToNodes(graph: TaskGraph): EditorNode[] {
  const rowByDepth = new Map<number, number>();
  return graph.nodes.map((graphNode) => {
    const depth = graphNode.depth;
    const row = rowByDepth.get(depth) ?? 0;
    rowByDepth.set(depth, row + 1);
    const kind = nodeKinds.includes(graphNode.kind as NodeKind)
      ? (graphNode.kind as NodeKind)
      : "transform";
    return {
      id: graphNode.id,
      type: "taskGraph",
      position: { x: depth * 172 + 18, y: row * 76 + 22 },
      data: { graphNode, label: graphNode.name, kind }
    };
  });
}

function graphToEdges(graph: TaskGraph): Edge[] {
  return graph.edges.map((edge) => ({
    id: `${edge.from}:${edge.to}`,
    source: edge.from,
    target: edge.to,
    label: edge.condition ?? undefined,
    className: "task-graph-edge"
  }));
}

function createsCycle(edges: TaskGraph["edges"], source: string, target: string): boolean {
  const outgoing = new Map<string, string[]>();
  for (const edge of edges) {
    outgoing.set(edge.from, [...(outgoing.get(edge.from) ?? []), edge.to]);
  }
  outgoing.set(source, [...(outgoing.get(source) ?? []), target]);
  const seen = new Set<string>();
  const visit = (id: string): boolean => {
    if (id === source) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return (outgoing.get(id) ?? []).some(visit);
  };
  return visit(target);
}

export function validateTaskGraph(graph: TaskGraph): string | null {
  if (graph.nodes.length === 0) return "Add at least one graph node";
  const ids = new Set(graph.nodes.map((node) => node.id));
  if (ids.size !== graph.nodes.length) return "Graph node IDs must be unique";
  if (graph.nodes.some((node) => node.name.trim().length === 0)) return "Every node needs a name";
  const edgeKeys = new Set<string>();
  for (const edge of graph.edges) {
    if (!ids.has(edge.from) || !ids.has(edge.to))
      return "Every edge must reference an existing node";
    if (edge.from === edge.to) return "A node cannot connect to itself";
    const key = `${edge.from}:${edge.to}`;
    if (edgeKeys.has(key)) return "Duplicate graph edges are not allowed";
    edgeKeys.add(key);
    const preceding = graph.edges.slice(0, graph.edges.indexOf(edge));
    if (createsCycle(preceding, edge.from, edge.to)) return "Task graphs must be acyclic";
  }
  return null;
}

export function normalizeTaskGraph(graph: TaskGraph): TaskGraph {
  const incoming = new Map(graph.nodes.map((node) => [node.id, 0]));
  const outgoing = new Map<string, string[]>();
  for (const edge of graph.edges) {
    incoming.set(edge.to, (incoming.get(edge.to) ?? 0) + 1);
    outgoing.set(edge.from, [...(outgoing.get(edge.from) ?? []), edge.to]);
  }
  const queue = graph.nodes.filter((node) => incoming.get(node.id) === 0).map((node) => node.id);
  const depths = new Map(queue.map((id) => [id, 0]));
  while (queue.length > 0) {
    const id = queue.shift();
    if (!id) break;
    for (const target of outgoing.get(id) ?? []) {
      depths.set(target, Math.max(depths.get(target) ?? 0, (depths.get(id) ?? 0) + 1));
      incoming.set(target, (incoming.get(target) ?? 1) - 1);
      if (incoming.get(target) === 0) queue.push(target);
    }
  }
  return {
    ...graph,
    nodes: graph.nodes.map((node) => ({ ...node, depth: depths.get(node.id) ?? 0 }))
  };
}

function NodeConfigurationFields({
  node,
  onChange
}: {
  node: GraphNode;
  onChange: (configuration: Record<string, unknown>) => void;
}) {
  const configuration = configurationObject(node.configuration);
  if (node.kind === "llm" || node.kind === "reviewer") {
    const request = configurationObject(configuration.request);
    return (
      <div className="task-node-protocol-fields">
        <label>
          Endpoint
          <select
            value={stringField(configuration, "endpoint") || "chat_completions"}
            onChange={(event) => onChange({ ...configuration, endpoint: event.target.value })}
          >
            <option value="chat_completions">Chat completions</option>
            <option value="responses">Responses</option>
            <option value="embeddings">Embeddings</option>
            <option value="image_generation">Image generation</option>
          </select>
        </label>
        <label>
          Model grant
          <input
            value={stringField(request, "model")}
            onChange={(event) =>
              onChange({ ...configuration, request: { ...request, model: event.target.value } })
            }
            placeholder="Profile model grant"
          />
        </label>
      </div>
    );
  }
  if (node.kind === "mcp_tool") {
    return (
      <div className="task-node-protocol-fields field-pair">
        <label>
          MCP server
          <input
            value={stringField(configuration, "server")}
            onChange={(event) => onChange({ ...configuration, server: event.target.value })}
          />
        </label>
        <label>
          Tool grant
          <input
            value={stringField(configuration, "tool")}
            onChange={(event) => onChange({ ...configuration, tool: event.target.value })}
          />
        </label>
      </div>
    );
  }
  if (node.kind === "a2a_delegation") {
    return (
      <div className="task-node-protocol-fields">
        <label>
          Agent grant
          <input
            value={stringField(configuration, "agent")}
            onChange={(event) => onChange({ ...configuration, agent: event.target.value })}
          />
        </label>
      </div>
    );
  }
  if (node.kind === "router") {
    return (
      <div className="task-node-protocol-fields">
        <label>
          Selected branch
          <input
            value={stringField(configuration, "selected")}
            onChange={(event) => onChange({ ...configuration, selected: event.target.value })}
            placeholder="Edge condition, node name, or node ID"
          />
        </label>
      </div>
    );
  }
  return null;
}

export function TaskGraphEditor({
  value,
  onChange,
  onValidationChange
}: {
  value: TaskGraph;
  onChange: (graph: TaskGraph) => void;
  onValidationChange: (error: string | null) => void;
}) {
  const [nodes, setNodes] = useState(() => graphToNodes(value));
  const [edges, setEdges] = useState(() => graphToEdges(value));
  const [selectedId, setSelectedId] = useState(value.nodes[0]?.id ?? null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [past, setPast] = useState<TaskGraph[]>([]);
  const [future, setFuture] = useState<TaskGraph[]>([]);
  const [configurationText, setConfigurationText] = useState(() =>
    JSON.stringify(value.nodes[0]?.configuration ?? {}, null, 2)
  );
  const [localError, setLocalError] = useState<string | null>(null);
  const [flow, setFlow] = useState<ReactFlowInstance<EditorNode> | null>(null);
  const [layoutRevision, setLayoutRevision] = useState(0);
  const selected = value.nodes.find((node) => node.id === selectedId) ?? null;
  const selectedEdge = value.edges.find((edge) => edgeId(edge) === selectedEdgeId) ?? null;
  const validation = useMemo(() => validateTaskGraph(value), [value]);
  useEffect(() => {
    onValidationChange(localError ?? validation);
  }, [localError, onValidationChange, validation]);
  useEffect(() => {
    if (!flow) return undefined;
    const frame = requestAnimationFrame(() => void flow.fitView({ padding: 0.18, duration: 0 }));
    return () => cancelAnimationFrame(frame);
  }, [flow, layoutRevision, nodes.length]);

  function commit(graph: TaskGraph) {
    setPast((current) => [...current.slice(-49), value]);
    setFuture([]);
    onChange(graph);
  }
  function emit(nextNodes: GraphNode[], nextEdges = value.edges, relayout = false) {
    const graph = normalizeTaskGraph({ ...value, nodes: nextNodes, edges: nextEdges });
    if (relayout) {
      setNodes(graphToNodes(graph));
      setEdges(graphToEdges(graph));
      setLayoutRevision((revision) => revision + 1);
    }
    commit(graph);
  }
  function restore(graph: TaskGraph) {
    const nextSelectedEdgeId =
      selectedEdgeId && graph.edges.some((edge) => edgeId(edge) === selectedEdgeId)
        ? selectedEdgeId
        : null;
    const nextSelectedId =
      nextSelectedEdgeId === null &&
      selectedId &&
      graph.nodes.some((node) => node.id === selectedId)
        ? selectedId
        : nextSelectedEdgeId === null
          ? (graph.nodes[0]?.id ?? null)
          : null;
    const nextSelected = graph.nodes.find((node) => node.id === nextSelectedId) ?? null;
    setNodes(graphToNodes(graph));
    setEdges(graphToEdges(graph));
    setLayoutRevision((revision) => revision + 1);
    setSelectedId(nextSelectedId);
    setSelectedEdgeId(nextSelectedEdgeId);
    setConfigurationText(JSON.stringify(nextSelected?.configuration ?? {}, null, 2));
    setLocalError(null);
    onChange(graph);
  }
  function undo() {
    const previous = past.at(-1);
    if (!previous) return;
    setPast((current) => current.slice(0, -1));
    setFuture((current) => [value, ...current].slice(0, 50));
    restore(previous);
  }
  function redo() {
    const next = future[0];
    if (!next) return;
    setFuture((current) => current.slice(1));
    setPast((current) => [...current.slice(-49), value]);
    restore(next);
  }
  function addNode(kind: NodeKind) {
    const id = crypto.randomUUID();
    const graphNode: GraphNode = {
      id,
      name: kindLabels[kind].toLowerCase().replaceAll(" ", "-"),
      kind,
      depth: 0,
      capability: null,
      side_effect: "none",
      configuration: kindConfiguration[kind],
      status: "pending",
      attempt_count: 0,
      idempotency_key: null,
      side_effect_confirmed: false,
      output: null,
      error_code: null,
      error_message: null,
      updated_at: ""
    };
    const index = nodes.length;
    const nextNodes = [
      ...nodes,
      {
        id,
        type: "taskGraph" as const,
        position: { x: (index % 2) * 164 + 18, y: Math.floor(index / 2) * 76 + 22 },
        data: { graphNode, label: graphNode.name, kind }
      }
    ];
    setNodes(nextNodes);
    setSelectedId(id);
    setSelectedEdgeId(null);
    setConfigurationText(JSON.stringify(graphNode.configuration, null, 2));
    emit([...value.nodes, graphNode]);
  }
  function updateSelected(patch: Partial<GraphNode>) {
    if (!selectedId) return;
    const nextGraphNodes = value.nodes.map((node) =>
      node.id === selectedId ? { ...node, ...patch } : node
    );
    setNodes((current) =>
      current.map((node) =>
        node.id === selectedId
          ? {
              ...node,
              data: {
                ...node.data,
                graphNode: { ...node.data.graphNode, ...patch },
                label: typeof patch.name === "string" ? patch.name : node.data.label
              }
            }
          : node
      )
    );
    emit(nextGraphNodes);
  }
  function updateSelectedConfiguration(configuration: Record<string, unknown>) {
    setConfigurationText(JSON.stringify(configuration, null, 2));
    setLocalError(null);
    updateSelected({ configuration });
  }
  function removeSelected() {
    if (!selectedId) return;
    const nextEdges = value.edges.filter(
      (edge) => edge.from !== selectedId && edge.to !== selectedId
    );
    setNodes((current) => current.filter((node) => node.id !== selectedId));
    setEdges((current) =>
      current.filter((edge) => edge.source !== selectedId && edge.target !== selectedId)
    );
    const nextNodes = value.nodes.filter((node) => node.id !== selectedId);
    setSelectedId(nextNodes[0]?.id ?? null);
    setSelectedEdgeId(null);
    emit(nextNodes, nextEdges, true);
  }
  function connect(connection: Connection) {
    if (!connection.source || !connection.target) return;
    const next = {
      from: connection.source,
      to: connection.target,
      condition: null,
      selected: null
    };
    if (
      value.edges.some((edge) => edge.from === next.from && edge.to === next.to) ||
      createsCycle(value.edges, next.from, next.to)
    ) {
      setLocalError("That connection would duplicate an edge or create a cycle");
      return;
    }
    setLocalError(null);
    setSelectedId(null);
    setSelectedEdgeId(`${next.from}:${next.to}`);
    emit(value.nodes, [...value.edges, next], true);
  }
  function selectNode(node: EditorNode) {
    setSelectedId(node.id);
    setSelectedEdgeId(null);
    setConfigurationText(JSON.stringify(node.data.graphNode.configuration, null, 2));
    setLocalError(null);
  }
  function changeNodes(changes: NodeChange<EditorNode>[]) {
    setNodes((current) => applyNodeChanges(changes, current));
    const removed = new Set(
      changes.filter((change) => change.type === "remove").map((change) => change.id)
    );
    if (removed.size === 0) return;
    const nextNodes = value.nodes.filter((node) => !removed.has(node.id));
    const nextEdges = value.edges.filter(
      (edge) => !removed.has(edge.from) && !removed.has(edge.to)
    );
    setEdges((current) =>
      current.filter((edge) => !removed.has(edge.source) && !removed.has(edge.target))
    );
    if (selectedId && removed.has(selectedId)) setSelectedId(nextNodes[0]?.id ?? null);
    emit(nextNodes, nextEdges, true);
  }
  function updateSelectedEdge(condition: string) {
    if (!selectedEdgeId) return;
    const graph = updateGraphEdgeCondition(value, selectedEdgeId, condition);
    setEdges(graphToEdges(graph));
    commit(normalizeTaskGraph(graph));
  }
  function removeSelectedEdge() {
    if (!selectedEdgeId) return;
    const nextEdges = value.edges.filter((edge) => edgeId(edge) !== selectedEdgeId);
    setSelectedEdgeId(null);
    emit(value.nodes, nextEdges, true);
  }

  return (
    <section
      className="task-graph-editor"
      aria-label="Task graph editor"
      onKeyDown={(event) => {
        if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "z") return;
        if (
          event.target instanceof HTMLInputElement ||
          event.target instanceof HTMLTextAreaElement ||
          event.target instanceof HTMLSelectElement
        )
          return;
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      }}
    >
      <div className="task-graph-actions">
        <span>
          {value.nodes.length} nodes / {value.edges.length} edges
        </span>
        <div>
          <button
            type="button"
            title="Undo graph change"
            disabled={past.length === 0}
            onClick={undo}
          >
            <Undo2 size={13} />
          </button>
          <button
            type="button"
            title="Redo graph change"
            disabled={future.length === 0}
            onClick={redo}
          >
            <Redo2 size={13} />
          </button>
        </div>
      </div>
      <div className="task-graph-palette" aria-label="Add graph node">
        {nodeKinds.map((kind) => (
          <button
            type="button"
            key={kind}
            onClick={() => addNode(kind)}
            title={`Add ${kindLabels[kind]}`}
          >
            <Plus size={12} /> {kindLabels[kind]}
          </button>
        ))}
      </div>
      <div className="task-graph-canvas">
        <ReactFlow<EditorNode>
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={changeNodes}
          onEdgesChange={(changes: EdgeChange<Edge>[]) => {
            setEdges((current) => applyEdgeChanges(changes, current));
            const removed = new Set(
              changes.filter((change) => change.type === "remove").map((change) => change.id)
            );
            if (removed.size > 0) {
              if (selectedEdgeId && removed.has(selectedEdgeId)) setSelectedEdgeId(null);
              emit(
                value.nodes,
                value.edges.filter((edge) => !removed.has(`${edge.from}:${edge.to}`)),
                true
              );
            }
          }}
          onConnect={connect}
          onNodeClick={(_, node) => selectNode(node)}
          onEdgeClick={(_, edge) => {
            setSelectedId(null);
            setSelectedEdgeId(edge.id);
            setLocalError(null);
          }}
          onPaneClick={() => {
            setSelectedId(null);
            setSelectedEdgeId(null);
          }}
          onInit={setFlow}
          fitView
          minZoom={0.45}
          maxZoom={1.4}
          deleteKeyCode={["Backspace", "Delete"]}
        >
          <Background gap={18} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
      {selected && (
        <div className="task-node-fields">
          <div className="task-node-heading">
            <span>{kindLabel(selected.kind)}</span>
            <button
              className="icon-button danger"
              type="button"
              title="Remove node"
              onClick={removeSelected}
            >
              <Trash2 size={14} />
            </button>
          </div>
          <div className="field-pair">
            <label>
              Name
              <input
                value={selected.name}
                onChange={(event) => updateSelected({ name: event.target.value })}
              />
            </label>
            <label>
              Side effect
              <select
                value={selected.side_effect}
                onChange={(event) => updateSelected({ side_effect: event.target.value })}
              >
                <option value="none">None</option>
                <option value="idempotent">Idempotent</option>
                <option value="destructive">Destructive</option>
                <option value="unknown">Unknown</option>
              </select>
            </label>
          </div>
          <label>
            Capability
            <input
              value={selected.capability ?? ""}
              onChange={(event) => updateSelected({ capability: event.target.value || null })}
              placeholder="Optional grant"
            />
          </label>
          <NodeConfigurationFields node={selected} onChange={updateSelectedConfiguration} />
          <details className="task-node-advanced">
            <summary>Advanced configuration JSON</summary>
            <label>
              Configuration
              <textarea
                className="mono"
                rows={6}
                value={configurationText}
                onChange={(event) => {
                  const text = event.target.value;
                  setConfigurationText(text);
                  try {
                    updateSelected({ configuration: JSON.parse(text) as unknown });
                    setLocalError(null);
                  } catch {
                    setLocalError("Node configuration must be valid JSON");
                  }
                }}
              />
            </label>
          </details>
        </div>
      )}
      {selectedEdge && (
        <div className="task-node-fields task-edge-fields">
          <div className="task-node-heading">
            <span>Route condition</span>
            <button
              className="icon-button danger"
              type="button"
              title="Remove connection"
              onClick={removeSelectedEdge}
            >
              <Trash2 size={14} />
            </button>
          </div>
          <p className="task-edge-route mono">
            {value.nodes.find((node) => node.id === selectedEdge.from)?.name ?? selectedEdge.from}
            <span>to</span>
            {value.nodes.find((node) => node.id === selectedEdge.to)?.name ?? selectedEdge.to}
          </p>
          <label>
            Condition
            <input
              value={selectedEdge.condition ?? ""}
              onChange={(event) => updateSelectedEdge(event.target.value)}
              placeholder="Optional router branch label"
            />
          </label>
        </div>
      )}
      {(localError ?? validation) && (
        <p className="form-message error">{localError ?? validation}</p>
      )}
    </section>
  );
}
